import type Phaser from "phaser";
import { CANVAS_COLOURS, standInBody, type FeetPoint, type StandInDefinition } from "daedalus-core";

/**
 * The editor's stand-in on the map (ADR-071 decisions 1 and 4): the registered art, one frame of a
 * spritesheet drawn with its pivot on the feet point, or, while the art is left out, loading or failed, a
 * `STAND_IN_COLOUR` box of the registered body size standing on the feet point with a darker edge, 2 screen
 * pixels wide at any zoom, and its feet marked by a dark line across the feet line. The feet box the inside test reads is outlined at the
 * stand-in's depth, with art or without. While selected, the body box is outlined over everything in the
 * selection colour, so a stand-in behind a wall can be found. The view draws what it is handed; where the
 * feet stand, the depth and the selection come from the host.
 */

/**
 * The box's fill, a bright green (the canvas colour table's `standIn`).
 */
export const STAND_IN_COLOUR = CANVAS_COLOURS.standIn.rgb;

/**
 * The box's edge, near-black so it stands clear of every ground and of the wash.
 */
export const STAND_IN_EDGE_COLOUR = CANVAS_COLOURS.standInEdge.rgb;

/**
 * The box's edge width in screen pixels at any zoom, inside the body size: the design's 2 px keyline, a
 * non-scaling stroke on the card.
 */
const STAND_IN_EDGE_WIDTH = 2;

/**
 * The line across the feet and the feet box's outline, a dark green that reads over the box, the art and
 * grass alike.
 */
export const STAND_IN_FEET_COLOUR = CANVAS_COLOURS.standInFeet.rgb;

/**
 * The outline of a selected stand-in: the colour the map tints a selected prop with.
 */
export const STAND_IN_SELECTION_COLOUR = CANVAS_COLOURS.selectedProp.rgb;

/**
 * How the stand-in is drawn: the box before its art has loaded or when there is none or it failed, and the
 * sheet's frame once loaded.
 */
export type StandInLook = "box" | "sheet";

/**
 * The prefix of the texture key a stand-in's sheet is loaded under.
 */
const TEXTURE_PREFIX = "daedalus:stand-in:";

/**
 * Draws one stand-in in a scene.
 */
export class StandInView {
  private readonly box: Phaser.GameObjects.Graphics;
  private readonly feetBox: Phaser.GameObjects.Graphics;
  private readonly outline: Phaser.GameObjects.Graphics;
  private sprite: Phaser.GameObjects.Image | null = null;
  private drawnAs: StandInLook = "box";
  private last: { feet: FeetPoint | null; depth: number; selected: boolean } = { feet: null, depth: 0, selected: false };
  private zoom = 1;

  /**
   * Draws the box and the outlines, hidden until `sync` stands the stand-in somewhere, and starts loading
   * the art when there is any; a sheet that fails to load leaves the box, with one warning.
   *
   * @param scene Scene to draw into.
   * @param standIn The registered stand-in.
   * @param selectionDepth The depth the selection outline draws at, over everything on the map.
   * @param warn Told once when the sheet fails to load.
   * @param drawn Told when the sheet's frame replaces the box.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly standIn: StandInDefinition,
    selectionDepth: number,
    warn: (message: string) => void,
    private readonly drawn: () => void = () => undefined,
  ) {
    const body = standInBody({ x: 0, y: 0 }, standIn);
    const width = body.x1 - body.x0 + 1;
    const height = body.y1 - body.y0 + 1;
    this.box = scene.add.graphics().setVisible(false);
    this.drawBox();
    const { halfWidth, height: feetHeight } = standIn.feetBox;
    this.feetBox = scene.add.graphics().setVisible(false);
    outlineRect(this.feetBox, STAND_IN_FEET_COLOUR, -halfWidth, -feetHeight, Math.max(1, halfWidth * 2), feetHeight);
    this.outline = scene.add.graphics().setVisible(false).setDepth(selectionDepth);
    outlineRect(this.outline, STAND_IN_SELECTION_COLOUR, body.x0 - 1, body.y0 - 1, width + 2, height + 2);
    this.loadArt(warn);
  }

  /**
   * How the stand-in is drawn now.
   *
   * @returns The box or the sheet's frame.
   */
  public get look(): StandInLook {
    return this.drawnAs;
  }

  /**
   * Follows the camera's zoom, so the box's edge stays `STAND_IN_EDGE_WIDTH` screen pixels wide: redraws the
   * box when the zoom differs from the one it was drawn for. Called every frame; an unchanged zoom compares
   * one number and allocates nothing.
   *
   * @param zoom The camera's zoom.
   * @returns True when the box was redrawn.
   */
  public setZoom(zoom: number): boolean {
    if (zoom === this.zoom) {
      return false;
    }
    this.zoom = zoom;
    this.drawBox();
    return true;
  }

  /**
   * Stands the stand-in at a feet point at a depth, or hides it.
   *
   * @param feet The feet point in whole world pixels, or null to hide it.
   * @param depth The depth it draws at (`standingDepth` of its feet line).
   * @param selected Whether it is selected.
   */
  public sync(feet: FeetPoint | null, depth: number, selected: boolean): void {
    this.last = { feet, depth, selected };
    const shown = feet !== null;
    const sprite = this.sprite;
    this.box.setVisible(shown && sprite === null);
    this.feetBox.setVisible(shown);
    this.outline.setVisible(shown && selected);
    sprite?.setVisible(shown);
    if (feet === null) {
      return;
    }
    this.box.setPosition(feet.x, feet.y).setDepth(depth);
    this.feetBox.setPosition(feet.x, feet.y).setDepth(depth);
    this.outline.setPosition(feet.x, feet.y);
    sprite?.setPosition(feet.x, feet.y).setDepth(depth);
  }

  /**
   * Removes everything the view drew.
   */
  public destroy(): void {
    this.box.destroy();
    this.feetBox.destroy();
    this.outline.destroy();
    this.sprite?.destroy();
    this.sprite = null;
  }

  /**
   * Draws the box about the feet point: the body in the edge colour, the fill inset by the edge's width in
   * world pixels at the current zoom, and the dark line across the feet. Position, depth and visibility stay.
   */
  private drawBox(): void {
    const body = standInBody({ x: 0, y: 0 }, this.standIn);
    const width = body.x1 - body.x0 + 1;
    const height = body.y1 - body.y0 + 1;
    const edge = STAND_IN_EDGE_WIDTH / this.zoom;
    const mark = Math.max(4, this.standIn.feetBox.halfWidth * 2 + 4);
    this.box.clear();
    this.box.fillStyle(STAND_IN_EDGE_COLOUR, 1).fillRect(body.x0, body.y0, width, height);
    this.box.fillStyle(STAND_IN_COLOUR, 1).fillRect(body.x0 + edge, body.y0 + edge, Math.max(0, width - 2 * edge), Math.max(0, height - 2 * edge));
    this.box.fillStyle(STAND_IN_FEET_COLOUR, 1).fillRect(-mark / 2, -1, mark, 1);
  }

  /**
   * Loads the art's sheet and draws its frame once loaded; with no art, or a sheet the loader refuses, the
   * box stays.
   *
   * @param warn Told once when the sheet fails to load.
   */
  private loadArt(warn: (message: string) => void): void {
    const art = this.standIn.art;
    if (art === undefined) {
      return;
    }
    const key = `${TEXTURE_PREFIX}${art.url}`;
    const ready = (): void => {
      if (this.sprite !== null || !this.box.active) {
        return;
      }
      this.sprite = this.scene.add.image(0, 0, key, art.frame).setOrigin(art.pivot.x / art.frameWidth, art.pivot.y / art.frameHeight);
      this.feetBox.setToTop();
      this.drawnAs = "sheet";
      this.sync(this.last.feet, this.last.depth, this.last.selected);
      this.drawn();
    };
    if (this.scene.textures.exists(key)) {
      ready();
      return;
    }
    const loader = this.scene.load;
    const complete = `filecomplete-spritesheet-${key}`;
    const loaded = (): void => {
      loader.off("loaderror", failed);
      ready();
    };
    const failed = (file: Phaser.Loader.File): void => {
      if (file.key !== key) {
        return;
      }
      loader.off(complete, loaded);
      loader.off("loaderror", failed);
      warn(`the player stand-in draws as a box: its sheet ${art.url} did not load`);
    };
    loader.once(complete, loaded);
    loader.on("loaderror", failed);
    loader.spritesheet(key, art.url, { frameWidth: art.frameWidth, frameHeight: art.frameHeight });
    loader.start();
  }
}

/**
 * Outlines a rectangle one pixel wide on its inside, in whole pixels, with filled strips rather than a
 * stroke, which Phaser centres on the edge and so smears across two pixels.
 *
 * @param graphics The graphics to draw into.
 * @param colour The colour.
 * @param x Left.
 * @param y Top.
 * @param width Width.
 * @param height Height.
 */
function outlineRect(graphics: Phaser.GameObjects.Graphics, colour: number, x: number, y: number, width: number, height: number): void {
  graphics.fillStyle(colour, 1);
  graphics.fillRect(x, y, width, 1);
  graphics.fillRect(x, y + height - 1, width, 1);
  graphics.fillRect(x, y, 1, height);
  graphics.fillRect(x + width - 1, y, 1, height);
}
